import React, { useEffect, useRef, useState, useContext } from 'react';
import './ChatApp.css';
import ChatSelection from './ChatSelection/ChatSelection';
import CourseSelection from './SubjectSelection/CourseSelection';
import { useOnClickOutside } from "usehooks-ts";
import { fetchStudentCourses } from "../../http/СhatAPI";
import classNames from "classnames";
import BoostChat from "./BoostChat/BoostChat";
import CourseChat from "./CourseChat/CourseChat";
import { useStores } from "../../index";
import { observer } from "mobx-react-lite";
import { Context } from "../../index";  // ✅ Берем контекст напрямую

export const ReceiverVariant = {
    Expert: 'Expert',
    Boost: 'Boost',
}

const ScreenMode = {
    Receiver: 'Receiver',
    Course: 'Course',
    Chat: 'Chat',
}

const screenBackMap = {
    [ScreenMode.Chat]: {
        [ReceiverVariant.Boost]: ScreenMode.Receiver,
        [ReceiverVariant.Expert]: ScreenMode.Course,
    },
    [ScreenMode.Course]: {
        [ReceiverVariant.Boost]: ScreenMode.Receiver,
        [ReceiverVariant.Expert]: ScreenMode.Receiver,
    }
}

const ChatApp = observer(() => {
    const { user, chat, aiChat } = useContext(Context); // ✅ Берем user из MobX-стора

    const [receiver, setReceiver] = useState(null);
    const [screen, setScreen] = useState(ScreenMode.Receiver);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [isChatOpen, setIsChatOpen] = useState(false);

    const [courses, setCourses] = useState([]);

    const chatAppRef = useRef(null);
    const buttonRef = useRef(null);

    const toggleChat = () => {
        setIsChatOpen(!isChatOpen);
        if (!isChatOpen) setScreen(ScreenMode.Receiver);
    };

    const goBack = () => {
        setScreen(screenBackMap[screen][receiver]);
    };

    const handleSelectCourse = (course) => {
        setScreen(ScreenMode.Chat);
        setSelectedCourse(course);

        if (receiver === ReceiverVariant.Expert && !chat.courseChatMap[course.id]) {
            chat.createChat(course.id);
        }
    }

    const handleSelectChat = (receiver) => {
        setReceiver(receiver);
        setScreen(receiver === ReceiverVariant.Expert ? ScreenMode.Course : ScreenMode.Chat);

        const courseId = user?.purchased_courses?.[0] || null; // ✅ Проверяем `user` из контекста

        if (receiver === ReceiverVariant.Boost && courseId && !aiChat.courseAiChat) {
            aiChat.createAiChat(courseId);
        }
    };

    useEffect(() => {
        if (!user?.isAuth || !user?.user || !user.user.id) {
            console.warn("Пользователь не авторизован.");
            return;
        }

        fetchStudentCourses(user?.user?.id)
            .then((response) => setCourses(response))
            .catch(console.error);
    }, [isChatOpen, user.isAuth, user?.id]); // ✅ Теперь вызываем ТОЛЬКО если `isAuth === true`



    useEffect(() => {
        document.body.classList.toggle('lock-scroll', isChatOpen);
    }, [isChatOpen, user.isAuth, user?.id]);

    useOnClickOutside([chatAppRef, buttonRef], () => {
        if (isChatOpen) setIsChatOpen(false);
    })

    return (
        <div className={classNames('chat-container', isChatOpen && 'active')}>
            <button ref={buttonRef} onClick={toggleChat} className="chat-icon-button">
                {isChatOpen ? (
                    <svg width="55" height="55" viewBox="0 0 55 55" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect width="55" height="55" rx="27.5" fill="#6446C3" />
                        <path
                            d="M16.4883 27.5391C16.4883 27.2461 16.6172 26.9766 16.8516 26.7539L24.6094 19.0078C24.8672 18.75 25.1133 18.6562 25.3945 18.6562C25.9688 18.6562 26.4258 19.0781 26.4258 19.6641C26.4258 19.9453 26.3203 20.2148 26.1328 20.4023L23.5078 23.0742L19.5703 26.6719L22.3945 26.4961H37.2188C37.8281 26.4961 38.25 26.9297 38.25 27.5391C38.25 28.1484 37.8281 28.582 37.2188 28.582H22.3945L19.5586 28.4062L23.5078 32.0039L26.1328 34.6758C26.3203 34.8516 26.4258 35.1328 26.4258 35.4141C26.4258 36 25.9688 36.4219 25.3945 36.4219C25.1133 36.4219 24.8672 36.3164 24.6328 36.0938L16.8516 28.3242C16.6172 28.1016 16.4883 27.832 16.4883 27.5391Z"
                            fill="white" />
                    </svg>
                ) : (
                    <svg width="55" height="55" viewBox="0 0 55 55" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect width="55" height="55" rx="27.5" fill="#6446C3" />
                        <path
                            d="M18.7383 39.7617C17.8359 39.7617 17.5781 39.0234 18.0352 38.4375C18.4805 37.8516 19.6523 36.5625 20.3086 35.4844C20.4023 35.3086 20.3438 35.1328 20.168 35.0391C16.4062 33.0234 14.0977 29.7305 14.0977 25.9805C14.0977 20.0859 20.0039 15.3281 27.3867 15.3281C34.7695 15.3281 40.6758 20.0859 40.6758 25.9805C40.6758 31.8164 35.0742 36.6211 26.625 36.6211C26.4727 36.6211 26.3203 36.6094 26.168 36.5977C26.0039 36.5977 25.8398 36.6562 25.6406 36.7969C23.6484 38.2266 20.1445 39.7617 18.7383 39.7617Z"
                            fill="white" />
                    </svg>
                )}
            </button>
            <div ref={chatAppRef} className={`chat-app ${isChatOpen ? 'visible' : ''}`}>
                {isChatOpen && (
                    <>
                        {screen === ScreenMode.Receiver &&
                            <ChatSelection onSelect={handleSelectChat} toggleChat={toggleChat} />}
                        {screen === ScreenMode.Course && (
                            <CourseSelection
                                courses={courses}
                                onSelect={handleSelectCourse}
                                onBack={goBack}
                                toggleChat={toggleChat}
                            />
                        )}
                        {screen === ScreenMode.Chat && receiver === ReceiverVariant.Boost &&
                            <BoostChat onBack={goBack} toggleChat={toggleChat} />}
                        {screen === ScreenMode.Chat && receiver === ReceiverVariant.Expert &&
                            <CourseChat course={selectedCourse} onBack={goBack} toggleChat={toggleChat} />}
                    </>
                )}
            </div>
        </div>
    );

})

export default ChatApp;
